import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import {
  AssetSize,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  ButtonFunction,
  ButtonType,
  Divider,
  DividerLine,
  Heading,
  HeadingLevel,
  HeadingTag,
  Icon,
  IconName,
  Loader,
  TextInput
} from '@enbw/ui-react'
import styles from './styles.module.scss'
import { SearchResults } from './components'
import { useContractSearch } from './services'
import { saveContractsSearchUrl } from '@/hooks/useBackToContractsSearch'
import { truncate } from '@/util/string-util'
import { usePageTitle } from '@/application/routing/hooks/usePageTitle'

const DEFAULT_PAGE_SIZE = 20
const QUERY_PARAM_QUERY = 'query'
const QUERY_PARAM_PAGE = 'page'
const QUERY_PARAM_PAGE_SIZE = 'pagesize'

export const ContractSearchPage: FC = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // URL is the source of truth for committed search state.
  // `page` in the URL is 1-indexed; the backend and internal state use 0-indexed.
  const urlQuery = searchParams.get(QUERY_PARAM_QUERY) ?? ''
  const urlPage = Math.max(0, (Number.parseInt(searchParams.get(QUERY_PARAM_PAGE) ?? '1') || 1) - 1)
  const urlPageSize = Number.parseInt(searchParams.get(QUERY_PARAM_PAGE_SIZE) ?? String(DEFAULT_PAGE_SIZE)) || DEFAULT_PAGE_SIZE

  // Local state for the controlled input — may differ from urlQuery while typing.
  const [searchQueryValue, setSearchQueryValue] = useState(urlQuery)

  // useQuery-based: fetches automatically when URL params change, and serves
  // cached results immediately when navigating back to the same search.
  const {
    data: searchResults,
    totalCount,
    isFetching,
    isSuccess
  } = useContractSearch({
    query: urlQuery,
    pageNumber: urlPage,
    pageSize: urlPageSize
  })

  const hasSearched = isSuccess && urlQuery !== ''

  const query = truncate(urlQuery, 15) || null
  usePageTitle(t('contracts.pageTitle', { query, context: query ? 'withQuery' : undefined }))

  // Reset the input when the URL query is cleared externally (e.g. clicking the nav link).
  useEffect(() => {
    if (urlQuery === '') {
      setSearchQueryValue('')
    }
  }, [urlQuery])

  // Persist the current search URL so the contract details back-button can restore it.
  useEffect(() => {
    const queryString = searchParams.toString()
    const url = queryString ? `/contracts?${queryString}` : '/contracts'
    saveContractsSearchUrl(url)
  }, [searchParams])

  // Debounced auto-search triggered by the user typing in the input.
  // Skips when the input matches the URL query — that means the value was set
  // from the URL (initial state), not from a user-driven change. This also
  // prevents React Strict Mode's double-invocation from resetting the URL page.
  useEffect(() => {
    if (searchQueryValue === urlQuery) return

    if (searchQueryValue.length === 0) {
      setSearchParams({}, { replace: true })
      return
    }

    if (searchQueryValue.length >= 3) {
      const debounceTimer = setTimeout(() => {
        const newParams: Record<string, string> = { [QUERY_PARAM_QUERY]: searchQueryValue }
        if (urlPageSize !== DEFAULT_PAGE_SIZE) newParams[QUERY_PARAM_PAGE_SIZE] = String(urlPageSize)
        setSearchParams(newParams, { replace: true })
      }, 250)

      return () => clearTimeout(debounceTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQueryValue])

  const handleSearch = (): void => {
    if (!searchQueryValue) return
    const newParams: Record<string, string> = { [QUERY_PARAM_QUERY]: searchQueryValue }
    if (urlPageSize !== DEFAULT_PAGE_SIZE) newParams[QUERY_PARAM_PAGE_SIZE] = String(urlPageSize)
    setSearchParams(newParams, { replace: true })
  }

  const handleSearchReset = (): void => {
    setSearchQueryValue('')
  }

  const handlePageChange = (newPage: number): void => {
    const newParams: Record<string, string> = { [QUERY_PARAM_QUERY]: urlQuery }
    if (newPage > 0) newParams[QUERY_PARAM_PAGE] = String(newPage + 1) // convert to 1-indexed for URL
    if (urlPageSize !== DEFAULT_PAGE_SIZE) newParams[QUERY_PARAM_PAGE_SIZE] = String(urlPageSize)
    setSearchParams(newParams, { replace: true })
  }

  const handlePageSizeChange = (newPageSize: number): void => {
    const newParams: Record<string, string> = { [QUERY_PARAM_QUERY]: urlQuery }
    if (newPageSize !== DEFAULT_PAGE_SIZE) newParams[QUERY_PARAM_PAGE_SIZE] = String(newPageSize)
    setSearchParams(newParams, { replace: true })
  }

  return (
    <div className={styles.contractSearchPage}>
      {/* Header Bar */}
      <div className={styles.headerBar}>
        <Breadcrumb>
          <BreadcrumbItem current>
            <span slot="label">{t('contracts.title')}</span>
          </BreadcrumbItem>
        </Breadcrumb>

        <Heading level={HeadingLevel.H1} tag={HeadingTag.H1}>
          {t('contracts.title')}
        </Heading>
      </div>

      <Divider line={DividerLine.Subtle} />

      {/* Search Input */}
      <div className={styles.searchForm}>
        <div className={styles.inputGroup}>
          <TextInput
            id="simple-search"
            placeholder={t('contracts.search.placeholder')}
            className={styles.simpleSearchInput}
            value={searchQueryValue}
            onInput={(e: Event) => setSearchQueryValue((e.target as unknown as HTMLInputElement).value)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') {
                handleSearch()
              }
            }}
          ></TextInput>
          {isFetching && <Loader />}
          {hasSearched && (
            <Button type={ButtonType.Reset} function={ButtonFunction.Secondary} label={t('contracts.search.reset')} onClick={() => handleSearchReset()}>
              <Icon slot="leading-icon" name={IconName.Undo} size={AssetSize.XSmall} />
            </Button>
          )}
        </div>
      </div>

      {/* Results Card */}
      <SearchResults
        hasSearched={hasSearched}
        contracts={searchResults ?? []}
        searchQuery={urlQuery}
        isPending={isFetching}
        totalCount={totalCount}
        currentPage={urlPage}
        pageSize={urlPageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  )
}
